'use client';

import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Calendar } from '../ui/calendar.tsx';
import { cn } from '../../lib/utils.ts';
import { EventModal } from './EventModal';
import {
  formatToISODateString,
  getScheduleDates as getScheduleDatesUtil,
} from '../../utils/dateUtils';

export function CalendarView({
  events,
  selectedDate,
  setSelectedDate,
  selectedEvent,
  isModalOpen,
  setIsModalOpen,
  onEventClick,
  onCreateEvent,
  onSaveEvent,
  onDeleteEvent,
}) {
  const [isMobile, setIsMobile] = useState(false);

  // Use the utility function for consistent date formatting
  const formatDateKey = (date) => formatToISODateString(date);

  // Use the utility function for generating schedule dates
  const getScheduleDates = (selectedDate) =>
    getScheduleDatesUtil(selectedDate, 1);

  // Get events for a specific date
  const getEventsForDate = (date) => {
    if (!date) return [];
    const filteredEvents = events.filter((event) => {
      return event.eventDate.split('T')[0] === date.toISOString().split('T')[0];
    });
    return filteredEvents;
  };

  // Calculate event style based on time (Google Calendar style)
  const calculateEventStyle = (startTime, endTime) => {
    const timeToMinutes = (time) => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    const duration = endMinutes - startMinutes;

    // Each hour is 48px high in our calendar
    const hourHeight = 48;
    const top = (startMinutes / 60) * hourHeight;
    const height = (duration / 60) * hourHeight;

    return {
      top: `${top}px`,
      height: `${height}px`,
      minHeight: '24px',
    };
  };

  const scheduleDates = getScheduleDates(selectedDate);
  let scheduleDatesFilter = [scheduleDates[0]]; // Will be used to save only the current selected date on mobile

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <div className="flex flex-col md:flex-row gap-6">
      <div className="w-full md:w-[400px] space-y-4 flex-[0.3] bg-white rounded-lg shadow-sm">
        <div className="flex items-center justify-between px-6 pt-2">
          <h2 className="text-lg font-medium">Calendar</h2>
          <Button
            size="icon"
            className="bg-[#7C3AED] hover:bg-[#7C3AED]/90"
            onClick={onCreateEvent}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => setSelectedDate(date || new Date())}
          events={events}
          className="rounded-lg bg-white"
          classNames={{
            day_selected:
              'bg-[#7C3AED] text-white hover:bg-[#7C3AED] hover:text-white focus:bg-[#7C3AED] focus:text-white',
            day_today: 'bg-purple-50 text-[#7C3AED]',
            day: 'text-gray-600 hover:bg-purple-50',
            cell: 'text-center text-sm p-0',
            head_cell: 'text-gray-500 font-normal',
            nav_button: 'text-gray-600 hover:bg-purple-50',
            table: 'border-collapse space-y-1',
          }}
          components={{
            DayContent: (props) => {
              if (!props.date) return null;

              const dateStr = formatDateKey(props.date);
              const dayEvents = events[dateStr] || [];

              return (
                <div className="relative">
                  <div>{props.date.getDate()}</div>
                  {dayEvents.length > 0 && (
                    <div className="absolute bottom-1 left-1/2 flex -translate-x-1/2 gap-0.5">
                      {dayEvents.slice(0, 3).map((event, i) => (
                        <div
                          key={i}
                          className={cn(
                            'h-1 w-1 rounded-full',
                            `bg-${event.color}-500`
                          )}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            },
          }}
        />
      </div>

      <div className="flex-[0.7] space-y-4 bg-white rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">My Schedule</h2>
          <Button
            variant="outline"
            className="text-[#7C3AED]"
            onClick={onCreateEvent}
          >
            + Add
          </Button>
        </div>

        <div className="flex flex-col">
          {/* Date headers row */}
          <div className="flex">
            <div className="w-16 flex-none" /> {/* Empty space for alignment */}
            {isMobile ? (
              <div className="flex flex-1 min-w-0 gap-4">
                {scheduleDatesFilter.map((day, i) => (
                  <div key={i} className="flex-1 min-w-[160px]">
                    <div
                      className={cn(
                        'sticky top-0 bg-white p-2 border-b text-center',
                        day.toDateString() === selectedDate.toDateString() &&
                          'bg-purple-50'
                      )}
                    >
                      <div className="text-sm text-muted-foreground truncate">
                        {day.toLocaleDateString('en-US', { weekday: 'long' })}
                      </div>
                      <div
                        className={cn(
                          'text-sm truncate',
                          day.toDateString() === selectedDate.toDateString() &&
                            'font-bold text-purple-600'
                        )}
                      >
                        {day.getDate()}{' '}
                        {day.toLocaleDateString('en-US', { month: 'long' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-1 min-w-0 gap-4">
                {scheduleDates.map((day, i) => (
                  <div key={i} className="flex-1 min-w-[160px]">
                    <div
                      className={cn(
                        'sticky top-0 bg-white p-2 border-b text-center',
                        day.toDateString() === selectedDate.toDateString() &&
                          'bg-purple-50'
                      )}
                    >
                      <div className="text-sm text-muted-foreground truncate">
                        {day.toLocaleDateString('en-US', { weekday: 'long' })}
                      </div>
                      <div
                        className={cn(
                          'text-sm truncate',
                          day.toDateString() === selectedDate.toDateString() &&
                            'font-bold text-purple-600'
                        )}
                      >
                        {day.getDate()}{' '}
                        {day.toLocaleDateString('en-US', { month: 'long' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Time grid */}
          <div className="flex max-h-[60vh] overflow-y-scroll">
            {/* Time labels column */}
            <div className="flex-none w-16">
              {Array.from({ length: 24 }, (_, i) => (
                <div
                  key={i}
                  className="h-12 text-xs text-gray-500 text-right pr-2 sticky left-0 bg-white"
                >
                  {i === 0
                    ? '12 AM'
                    : i < 12
                    ? `${i} AM`
                    : i === 12
                    ? '12 PM'
                    : `${i - 12} PM`}
                </div>
              ))}
            </div>

            {/* Events grid */}
            {isMobile ? (
              <div className="flex flex-1 min-w-0 gap-4">
                {scheduleDatesFilter.map((day, i) => {
                  const dayEvents = getEventsForDate(day);
                  return (
                    <div key={i} className="flex-1 min-w-[160px]">
                      <div
                        className="relative"
                        style={{ height: `${24 * 48}px` }}
                      >
                        {/* Hour grid lines */}
                        {Array.from({ length: 24 }, (_, hour) => (
                          <div
                            key={hour}
                            className="absolute left-0 right-0 border-t border-gray-100"
                            style={{ top: `${hour * 48}px` }}
                          />
                        ))}

                        {/* Half-hour grid lines */}
                        {Array.from({ length: 24 }, (_, hour) => (
                          <div
                            key={hour}
                            className="absolute left-0 right-0 border-t border-dotted border-gray-100"
                            style={{ top: `${hour * 48 + 24}px` }}
                          />
                        ))}

                        {/* Events */}
                        {dayEvents.length === 0 && <p>No events</p>}

                        {dayEvents.length > 0 &&
                          dayEvents.map((event) => {
                            // Ensure time has start and end properties for backward compatibility
                            const timeStart =
                              event.startTime
                                .split('T')[1]
                                .split('.')[0]
                                .slice(0, 5) || '09:00';
                            const timeEnd =
                              event.endTime
                                .split('T')[1]
                                .split('.')[0]
                                .slice(0, 5) || '10:00';

                            return (
                              <div
                                key={event.id}
                                onClick={() => onEventClick(event)}
                                className={`
                                absolute left-1 right-1 rounded p-1 text-xs bg-purple-600 text-white hover:bg-purple-700 hover:text-white overflow-hidden cursor-pointer hover:opacity-90`}
                                style={calculateEventStyle(timeStart, timeEnd)}
                              >
                                <div className="font-medium truncate">
                                  {event.eventTitle}
                                </div>
                                <div className="text-xs opacity-90 truncate">
                                  {timeStart} - {timeEnd}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-1 min-w-0 gap-4">
                {scheduleDates.map((day, i) => {
                  const dayEvents = getEventsForDate(day);
                  return (
                    <div key={i} className="flex-1 min-w-[160px]">
                      <div
                        className="relative"
                        style={{ height: `${24 * 48}px` }}
                      >
                        {/* Hour grid lines */}
                        {Array.from({ length: 24 }, (_, hour) => (
                          <div
                            key={hour}
                            className="absolute left-0 right-0 border-t border-gray-100"
                            style={{ top: `${hour * 48}px` }}
                          />
                        ))}

                        {/* Half-hour grid lines */}
                        {Array.from({ length: 24 }, (_, hour) => (
                          <div
                            key={hour}
                            className="absolute left-0 right-0 border-t border-dotted border-gray-100"
                            style={{ top: `${hour * 48 + 24}px` }}
                          />
                        ))}

                        {/* Events */}
                        {dayEvents.length === 0 && <p>No events</p>}

                        {dayEvents.length > 0 &&
                          dayEvents.map((event) => {
                            // Ensure time has start and end properties for backward compatibility
                            const timeStart =
                              event.startTime
                                .split('T')[1]
                                .split('.')[0]
                                .slice(0, 5) || '09:00';
                            const timeEnd =
                              event.endTime
                                .split('T')[1]
                                .split('.')[0]
                                .slice(0, 5) || '10:00';

                            return (
                              <div
                                key={event.id}
                                onClick={() => onEventClick(event)}
                                className={`
                                absolute left-1 right-1 rounded p-1 text-xs bg-purple-600 text-white hover:bg-purple-700 hover:text-white overflow-hidden cursor-pointer hover:opacity-90`}
                                style={calculateEventStyle(timeStart, timeEnd)}
                              >
                                <div className="font-medium truncate">
                                  {event.eventTitle}
                                </div>
                                <div className="text-xs opacity-90 truncate">
                                  {timeStart} - {timeEnd}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <EventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        event={selectedEvent}
        onSave={onSaveEvent}
        onDelete={onDeleteEvent}
      />
    </div>
  );
}
